/**
 * Nama orang yang sedang masuk — MURNI.
 *
 * Nama milik HBS, bukan Workforce: `MeResponse` hanya membawa id, peran, dan cakupan. Ia diambil
 * dari roster anggota organisasi, endpoint yang SENGAJA tidak ber-`@Roles` di HBS — setiap anggota
 * aktif boleh memanggilnya, termasuk kurir.
 */

export interface RosterEntry {
  readonly id: string;
  readonly displayName: string;
}

/** Penanda HBS untuk anggota tanpa profil. Diperlakukan sama dengan tidak ada nama. */
const BELUM_DIISI = '(nama belum diisi)';

/**
 * Struktur sama dengan `bersih.includes('@')` dkk di `inviteProblem` (`invite.ts`) — bukan
 * validator RFC, hanya cukup untuk mengenali pola "ini alamat email", bukan menerima/menolaknya.
 */
function looksLikeEmail(value: string): boolean {
  return value.includes('@') && !value.startsWith('@') && !value.endsWith('@') && !/\s/.test(value);
}

/**
 * Satu-satunya tempat yang memutuskan apakah sebuah `displayName` dari HBS layak ditampilkan
 * sebagai nama, atau `null` bila tidak.
 *
 * ADA KARENA hal yang sama ditulis ulang lima kali di lima berkas (`rekap-bulanan.ts`,
 * `attendance-roster.ts`, `worker-candidates.ts`, `pengaju-koreksi.ts`, dan fungsi ini sendiri),
 * masing-masing dengan komentar yang SAMA PERSIS niatnya ("bukan email, bukan UUID") tetapi TIDAK
 * SATU PUN benar-benar memeriksa bentuk email — hanya string kosong. HBS mengisi `displayName`
 * dengan alamat email saat profil belum dilengkapi (bukti: `bangdadi09@gmail.com` tampil apa
 * adanya di Rekap Bulanan, temuan review 12 Sep 2026 item 3), dan kelima tempat itu meloloskannya
 * begitu saja karena stringnya tidak kosong.
 */
export function namaLayak(nama: string | null | undefined): string | null {
  if (typeof nama !== 'string') return null;
  const bersih = nama.trim();
  if (bersih.length === 0) return null;
  if (bersih === BELUM_DIISI) return null;
  if (looksLikeEmail(bersih)) return null;
  return bersih;
}

/**
 * Nama untuk disapa, atau `null` bila tidak ada yang layak ditampilkan.
 *
 * `null` — bukan email, bukan UUID. Menyapa orang dengan alamat emailnya terbaca seperti sistem
 * yang tidak mengenalnya, dan UUID lebih buruk lagi. Pemanggil menampilkan sapaan tanpa nama.
 */
export function displayNameOf(roster: readonly RosterEntry[], userId: string): string | null {
  const saya = roster.find((m) => m.id === userId);
  if (!saya) return null;
  return namaLayak(saya.displayName);
}

/**
 * Sapaan. Dipisah dari pencarian namanya supaya bentuk kalimatnya dapat diuji sendiri.
 *
 * Tanpa nama, sapaan tetap utuh — "Halo" adalah kalimat yang sah, sedangkan "Halo, " dengan koma
 * menggantung adalah kalimat yang rusak.
 */
export function greeting(name: string | null): string {
  return name === null ? 'Halo' : `Halo, ${name}`;
}

/**
 * Baris identitas header (item 12, review 12 Sep 2026): "nama org · nama user · peran".
 *
 * Bagian yang belum termuat (nama org/nama user datang lewat panggilan HBS terpisah, async) atau
 * memang tidak ada dilewati diam-diam — BUKAN ditulis sebagai "· ·" yang kosong di tengah, dan
 * bukan pula menahan seluruh baris menunggu bagian paling lambat. Header yang tampil bertahap
 * (peran dulu, lalu nama menyusul) lebih jujur daripada header yang diam sampai semuanya siap.
 * Kosong total (belum ada apa pun) jatuh ke nama aplikasi — bukan baris tanpa isi.
 */
export function headerLine(parts: readonly (string | null)[]): string {
  const terisi = parts.filter((p): p is string => p !== null && p.trim().length > 0);
  return terisi.length > 0 ? terisi.join(' · ') : 'SalesHub Pro Workforce';
}
